import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useNotificationContext } from "contexts";
import apiService from "services/api";
import { removeEmptyStringFields } from "utils/objectCleaning";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { useRef, useReducer } from "react";
import {
  initialState,
  initialAddCoursePurchaseModalReducerState,
} from "../constants";
import {
  addCoursePurchaseModalReducer,
  addCoursePurchaseModalReducerActions,
} from "../reducers";
import { RequiredFields } from "./RequiredFields";
import { OptionalFields } from "./OptionalFields";
import { AddCoursePurchaseModal } from "./AddCoursePurchaseModal";
import { EditStudentPageButtonGroup } from "./EditStudentPageButtonGroup";
import { StudentCourses } from "./StudentCourses";

const EditStudentPage = () => {
  const { student_id } = useParams();
  const fileInputRef = useRef();
  const [addCoursePurchaseModalState, addCoursePurchaseModalDispatch] =
    useReducer(
      addCoursePurchaseModalReducer,
      initialAddCoursePurchaseModalReducerState,
    );

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: initialState,
    revalidateMode: "onChange",
  });

  const { data: parentOptions, isLoading: isParentLoading } = useQuery(
    ["parent"],
    () =>
      apiService.parent.get().then((parents) =>
        parents?.map(
          (parent) =>
            ({
              value: parent.id,
              label: `${parent.firstName} ${parent.lastName}`,
            }) || [],
        ),
      ),
  );

  const { data: centres, isLoading: isCentreLoading } = useQuery(
    ["centre"],
    () =>
      apiService.student.centres().then((centres) =>
        centres.map((centre) => ({
          label: centre.shortName,
          value: centre.id,
          data: centre,
        })),
      ),
  );

  const transformData = (student) =>
    reset({
      ...student,
      parentId: student?.parent?.id,
      centreId: student?.centre?.id,
    });

  const {
    data: student,
    isLoading: isStudentLoading,
    refetch,
  } = useQuery(
    ["student", student_id],
    () => apiService.student.getLeanV2(student_id),
    {
      onSuccess: transformData,
      enabled: Boolean(student_id),
    },
  );

  const { createNotification } = useNotificationContext();

  const editStudent = async (data) => {
    const formData = removeEmptyStringFields(data);

    apiService.student
      .update(student_id, formData)
      .then(() => {
        createNotification({
          type: "success",
          message: "Edit Student Successful",
        });
        refetch();
      })
      .catch((error) =>
        createNotification({ type: "danger", message: error.message }),
      );
  };

  const resetAndCancel = () => {
    transformData(student);
  };

  return (
    <>
      <AddCoursePurchaseModal
        show={addCoursePurchaseModalState.show}
        hideModal={() =>
          addCoursePurchaseModalDispatch({
            type: addCoursePurchaseModalReducerActions.HIDE,
          })
        }
        studentId={student_id}
        refetch={refetch}
      />
      <form onSubmit={handleSubmit(editStudent)} className="content">
        <div className="d-flex justify-content-center">
          <div className="w-100">
            <h4 className="text-center mb-4">Edit Student</h4>
            <EditStudentPageButtonGroup
              resetAndCancel={resetAndCancel}
              studentId={student_id}
            />
            <RequiredFields
              register={register}
              errors={errors}
              isParentLoading={isParentLoading}
              isStudentLoading={isStudentLoading}
              fileInputRef={fileInputRef}
              nftPath={student?.nftPath}
              control={control}
              parentOptions={parentOptions}
              isCentreLoading={isCentreLoading}
              centres={centres || []}
            />
            {/* <StudentCourses student={student} /> */}
            <OptionalFields register={register} errors={errors} />
          </div>
        </div>
      </form>
    </>
  );
};

export default EditStudentPage;
